// src/components/CycleSettings/cycle-settings.helpers.ts

import CycleSettings from '../../../enums/cycle-setting.enum';
import { IDbCycleSettings } from '../../../store/models/cycles';
import {
  ChannelData,
  PostWeldRecipe,
  PostWeldSettings,
  RecipeStep,
} from '../../../store/models/types/cycle/ICycleIoTChannelsSettings';
import { IRecipeStepEditable, StepMode } from '../../../types/recipes-types';
import CycleMeasurement from '../../../enums/cycle-measurement';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../../utils/temperature-unit-conversion';
import IconCheck from '../../Icons/IconCheck/IconCheck';
import IconCrossRound from '../../Icons/IconCrossRound/IconCrossRound';

export type DiffResult = { nameProperty: string; differences: { db: any; channel: any } };

// Only DB-sourced steps carry an `isCelsius` flag — raw channel/IoT steps don't,
// so callers must supply the channel's measurement separately (see getStepDiff).
const getStepMeasurement = (step?: IRecipeStepEditable): CycleMeasurement =>
  step?.isCelsius === false ? CycleMeasurement.Fahrenheit : CycleMeasurement.Celsius;

const convertDeltaForDisplay = (value: number, from: CycleMeasurement, to: CycleMeasurement): number =>
  convertTemperatureDelta(value, from, to) as number;

const convertAbsoluteForDisplay = (value: number, from: CycleMeasurement, to: CycleMeasurement): number =>
  convertAbsoluteTemperature(value, from, to) as number;

const getChannelMeasurement = (channelSettings: ChannelData): CycleMeasurement =>
  channelSettings.units ?? CycleMeasurement.Celsius;

// When the controller's unit differs from the recipe's own unit, the value sent to the
// controller and the value read back both go through a C<->F conversion (once on the way
// out, once when comparing here), which can round-trip to within 1 degree of the original
// instead of landing exactly on it. Only widen the equality check when a conversion was
// actually involved - same-unit comparisons still require an exact match.
const UNIT_MISMATCH_TOLERANCE = 1;

const valuesMatch = (dbValue: number, channelValue: number, unitsDiffer: boolean): boolean => {
  if (unitsDiffer) {
    return Math.abs(dbValue - channelValue) <= UNIT_MISMATCH_TOLERANCE;
  }

  return dbValue === channelValue;
};

const getStepDiff = (
  stepChannel: RecipeStep,
  stepDb: IRecipeStepEditable,
  order: number,
  channelMeasurement: CycleMeasurement,
  measurement: CycleMeasurement,
) => {
  // The cloud clamps negative temperatures to 0 before sending to the controller, but a
  // controller can still hold older negative legacy values in fields its current step mode
  // doesn't actually use (e.g. a leftover "Rate End Temperature" on a Hold step). Only compare
  // fields the recipe's step mode actually uses, based on what the DB recipe expects.
  const isRateStep = stepDb.mode === StepMode.Rate;

  const keysToCompare: (keyof RecipeStep)[] = [
    'mode',
    'order',
    ...(isRateStep
      ? (['heatingRate', 'heatingLimit'] as (keyof RecipeStep)[])
      : (['durationMinutesTotal', 'durationDays', 'durationHours', 'durationMinutes'] as (keyof RecipeStep)[])),
  ];

  const diffs: DiffResult[] = [];

  const stepMeasurement = getStepMeasurement(stepDb);
  const unitsDiffer = channelMeasurement !== stepMeasurement;

  keysToCompare.forEach((key) => {
    let channelValue = stepChannel[key];
    let dbValue = stepDb[key];
    let keyLabel = key as string;
    let isTemperatureField = false;
    if (key === 'heatingRate') {
      keyLabel = 'Rate';
      isTemperatureField = true;
      // `stepChannel` is raw IoT data and has no `isCelsius` field, so it must use the
      // channel's actual measurement here rather than `getStepMeasurement`, which would
      // always misread it as Celsius.
      channelValue = convertDeltaForDisplay(stepChannel.heatingRate, channelMeasurement, measurement);
      dbValue = convertDeltaForDisplay(stepDb.heatingRate, stepMeasurement, measurement);
    }
    if (key === 'heatingLimit') {
      keyLabel = 'Rate End Temperature';
      isTemperatureField = true;
      channelValue = convertAbsoluteForDisplay(stepChannel.heatingLimit, channelMeasurement, measurement);
      dbValue = convertAbsoluteForDisplay(stepDb.heatingLimit, stepMeasurement, measurement);
    }

    const isMatch = isTemperatureField
      ? valuesMatch(dbValue as number, channelValue as number, unitsDiffer)
      : channelValue === dbValue;

    if (!isMatch) {
      diffs.push({
        nameProperty: `Step ${order} field "${keyLabel}"`,
        differences: { db: dbValue, channel: channelValue },
      });
    }
  });

  return {
    isEqual: Object.keys(diffs).length === 0,
    differences: diffs,
  };
};

const diffRecipes = (
  dbRecipe: IDbCycleSettings,
  postRecipe: PostWeldRecipe,
  channelMeasurement: CycleMeasurement,
  measurement: CycleMeasurement,
): DiffResult[] => {
  const fields: Array<[keyof IDbCycleSettings, keyof PostWeldRecipe]> = [
    ['name', 'recipeName'],
    ['rateUpperTolerance', 'upperRampTolerance'],
    ['rateLowerTolerance', 'lowerRampTolerance'],
    ['holdUpperTolerance', 'upperHoldTolerance'],
    ['holdLowerTolerance', 'lowerHoldTolerance'],
  ];

  const diffs: DiffResult[] = [];

  // Recipe-level tolerance fields are always stored in Celsius, regardless of the step's
  // own unit, so the controller's unit only needs to be compared against Celsius here.
  const unitsDiffer = channelMeasurement !== CycleMeasurement.Celsius;

  // Compare mapped fields
  for (const [dbKey, postKey] of fields) {
    const isTemperatureField = dbKey !== 'name';
    const dbValue = isTemperatureField
      ? convertDeltaForDisplay(dbRecipe[dbKey] as number, CycleMeasurement.Celsius, measurement)
      : dbRecipe[dbKey];
    const channelValue = isTemperatureField
      ? convertDeltaForDisplay(postRecipe[postKey] as number, channelMeasurement, measurement)
      : postRecipe[postKey];

    const isMatch = isTemperatureField
      ? valuesMatch(dbValue as number, channelValue as number, unitsDiffer)
      : dbValue === channelValue;

    if (!isMatch) {
      diffs.push({
        nameProperty: `Recipe field "${dbKey}"`,
        differences: {
          db: dbValue,
          channel: channelValue,
        },
      });
    }
  }

  // Special case: steps/numberOfSteps
  if ((dbRecipe.steps?.length ?? 0) !== (postRecipe.numberOfSteps ?? 0)) {
    diffs.push({
      nameProperty: 'Number of Steps',
      differences: {
        db: dbRecipe.steps?.length ?? 0,
        channel: postRecipe.numberOfSteps ?? 0,
      },
    });
  }

  return diffs;
};

const compareChannelWithDbRecipe = (
  channelSettings: PostWeldSettings,
  dbRecipe: IDbCycleSettings,
  channelMeasurement: CycleMeasurement,
  measurement: CycleMeasurement,
) => {
  const channelStep = channelSettings.recipe;

  const channelSteps = channelSettings.recipeSteps ?? [];
  const dbRecipeSteps = dbRecipe.steps ?? [];

  const allOrders = Array.from(
    new Set([...channelSteps.map((s) => s.order), ...dbRecipeSteps.map((s) => s.order)]),
  ).sort((a, b) => a - b);

  let differences: DiffResult[] = [];

  const recipeDifferences = diffRecipes(dbRecipe, channelStep, channelMeasurement, measurement);
  if (Object.keys(recipeDifferences).length > 0) {
    differences = differences.concat(recipeDifferences);
  }

  for (const order of allOrders) {
    const channelStep = channelSteps.find((s) => s.order === order);
    const dbStep = dbRecipeSteps.find((s) => s.order === order);

    if (!channelStep || !dbStep) continue;

    const normalize = (s?: RecipeStep | IRecipeStepEditable) =>
      s
        ? {
            ...s,
            heatingRate: Math.abs(s.heatingRate ?? 0),
            heatingLimit: Math.abs(s.heatingLimit ?? 0),
          }
        : undefined;

    const normChannel = normalize(channelStep) as RecipeStep;
    const normDb = normalize(dbStep) as IRecipeStepEditable;

    const diff = getStepDiff(normChannel, normDb, order, channelMeasurement, measurement);

    if (!diff.isEqual) {
      differences = differences.concat(diff.differences);
    }
  }

  return differences;
};

export const getPostWeldSettings = (
  channels: Record<string, ChannelData>,
  mode: CycleSettings,
  recipeByDB: IDbCycleSettings,
  getDataDiffs: (channelId: string, diffs: DiffResult[]) => void,
  measurement: CycleMeasurement,
) => {
  return Object.entries(channels).map(([channelId, channelSettings]) => {
    const { mode: itemMode, settingsByMode } = channelSettings;
    const channelSettingsByMode = settingsByMode[CycleSettings.PostWeld]?.settings;

    const recipe = channelSettingsByMode?.recipe;

    if (!recipe) {
      return {
        name: channelId,
        mode: itemMode,
        upperRampTolerance: 0,
        lowerRampTolerance: 0,
        upperHoldTolerance: 0,
        lowerHoldTolerance: 0,
        hasDifferences: '-',
        isSameMode: itemMode === mode,
      };
    }

    const channelMeasurement = getChannelMeasurement(channelSettings);
    const differences = compareChannelWithDbRecipe(channelSettingsByMode, recipeByDB, channelMeasurement, measurement);

    return {
      name: channelId,
      mode: itemMode,
      upperRampTolerance: convertDeltaForDisplay(recipe.upperRampTolerance, channelMeasurement, measurement),
      lowerRampTolerance: convertDeltaForDisplay(recipe.lowerRampTolerance, channelMeasurement, measurement),
      upperHoldTolerance: convertDeltaForDisplay(recipe.upperHoldTolerance, channelMeasurement, measurement),
      lowerHoldTolerance: convertDeltaForDisplay(recipe.lowerHoldTolerance, channelMeasurement, measurement),
      hasDifferences:
        differences.length === 0 ? (
          <IconCheck iconClassName="max-w-fit" fill="#16a34a" />
        ) : (
          <IconCrossRound
            iconClassName="max-w-fit"
            fill="#FF0000"
            onClick={() => getDataDiffs(channelId, differences)}
          />
        ),
      isSameMode: itemMode === mode,
    };
  });
};

export const getPreHeatSettings = (
  channels: Record<string, ChannelData>,
  mode: CycleSettings,
  measurement: CycleMeasurement,
) => {
  return Object.entries(channels).map(([channelId, channelSettings]) => {
    const { mode: itemMode, settingsByMode } = channelSettings;
    const settings = settingsByMode[CycleSettings.PreHeat]?.settings;

    return {
      name: channelId,
      mode: itemMode,
      holdingTemperature: convertAbsoluteForDisplay(
        settings?.finalTemperature ?? 0,
        getChannelMeasurement(channelSettings),
        measurement,
      ),
      rate: convertDeltaForDisplay(settings?.heatingRate ?? 0, getChannelMeasurement(channelSettings), measurement),
      soakUpper: convertDeltaForDisplay(
        settings?.soakUpperTolerance ?? 0,
        getChannelMeasurement(channelSettings),
        measurement,
      ),
      soakLower: convertDeltaForDisplay(
        settings?.soakLowerTolerance ?? 0,
        getChannelMeasurement(channelSettings),
        measurement,
      ),
      isSameMode: itemMode === mode,
    };
  });
};

export const getPercentageTimerSettings = (channels: Record<string, ChannelData>, mode: CycleSettings) => {
  return Object.entries(channels).map(([channelId, channelSettings]) => {
    const { mode: itemMode, settingsByMode } = channelSettings;
    const settings = settingsByMode[CycleSettings.PercentageTimer]?.settings;

    return {
      name: channelId,
      mode: itemMode,
      pt: settings?.PT ?? 0, // Use optional chaining here too for safety
      isSameMode: itemMode === mode,
    };
  });
};
